import { ascii, view, type BlockReader } from './reader';
import { parseComments, parsePictureBlock } from './vorbis';
import type { MediaInfo } from './index';

/**
 * Chapters, cover art and exact length from a FLAC file's metadata blocks,
 * which all sit at the front: STREAMINFO (length), VORBIS_COMMENT (chapters),
 * PICTURE (cover). Padding and seek tables are skipped without being read.
 */

const MAX_BLOCKS = 128;
const MAX_BLOCK_BYTES = 16 * 1024 * 1024;
const STREAMINFO = 0;
const VORBIS_COMMENT = 4;
const PICTURE = 6;
const FRONT_COVER = 3;

/** `start` is where "fLaC" begins — after an ID3 tag, if the file has one. */
export async function readFlac(r: BlockReader, start = 0): Promise<MediaInfo> {
  if (ascii(await r.bytes(start, 4), 0) !== 'fLaC') return { chapters: [] };

  const info: MediaInfo = { chapters: [] };
  let at = start + 4;

  for (let i = 0; i < MAX_BLOCKS; i++) {
    const h = await r.bytes(at, 4);
    if (h.length < 4) break;
    const last = ((h[0] ?? 0) & 0x80) !== 0;
    const type = (h[0] ?? 0) & 0x7f;
    const length = ((h[1] ?? 0) << 16) | ((h[2] ?? 0) << 8) | (h[3] ?? 0);
    at += 4;

    const wanted = type === STREAMINFO || type === VORBIS_COMMENT || type === PICTURE;
    if (wanted && length <= MAX_BLOCK_BYTES) {
      const b = await r.bytes(at, length);
      if (type === STREAMINFO && b.length >= 18) {
        const v = view(b);
        const rate = (v.getUint32(10) >>> 12) & 0xfffff;
        const samples = ((b[13] ?? 0) & 0x0f) * 2 ** 32 + v.getUint32(14);
        if (rate && samples) info.durationSec = samples / rate;
      } else if (type === VORBIS_COMMENT) {
        const parsed = parseComments(b);
        info.chapters = parsed.chapters;
        if (parsed.picture && !info.cover) info.cover = parsed.picture;
      } else if (type === PICTURE) {
        const picture = parsePictureBlock(b);
        // A front cover beats whatever was found first.
        if (picture && (!info.cover || picture.type === FRONT_COVER)) {
          info.cover = { mimeType: picture.mimeType, data: picture.data };
        }
      }
    }

    at += length;
    if (last) break;
  }

  if (info.cover) info.cover = { mimeType: info.cover.mimeType, data: info.cover.data };
  return info;
}
